import { useCallback } from "react";
import { isValidEmail } from "../utils/userManagementUtils";

export const useUserOperations = ({
  userForm,
  selectedDepartment,
  selectedUserDivision,
  activeTab,
  academyId,
  getRoleIdForTab,
  createUser,
  updateUser,
  selectedUserId,
  showSuccessToast,
  showErrorAlert,
  resetUserForm,
  refetchUsers,
  setShowModal,
  usersData,
}) => {
  const handleCreateUser = useCallback(async () => {
    const isRequiredFieldMissing =
      !userForm.name ||
      !userForm.email ||
      !userForm.password ||
      !userForm.employeeId ||
      !selectedDepartment ||
      ((activeTab === "Supervisor" || activeTab === "Technician") &&
        !selectedUserDivision);

    if (isRequiredFieldMissing) {
      showErrorAlert("Please fill in all required fields");
      return;
    }

    // Check if manager already exists for this academy
    if (activeTab === "Manager") {
      const existingManager = usersData?.find((user) => {
        const userRole = user.role?.name?.toLowerCase();
        const isManager = userRole === "manager";
        const sameAcademy = user.academyId === academyId;
        return isManager && sameAcademy;
      });

      if (existingManager) {
        showErrorAlert(
          `This academy already has a manager (${existingManager.name}). Each academy can only have one manager.`
        );
        return;
      }
    }

    const deptId = selectedDepartment?.value;
    
    console.log("🛠️ Validation Check:");
    console.log("  - selectedDepartment object:", selectedDepartment);
    console.log("  - Extracted deptId:", deptId);
    console.log("  - academyId:", academyId);

    if (!deptId || deptId === "undefined" || deptId === "null") {
      console.error("❌ Aborting create user: Invalid department ID", deptId);
      showErrorAlert("Please select a valid department from the dropdown list.");
      return;
    }

    if (!academyId) {
      console.error("❌ Aborting create user: academyId is missing");
      showErrorAlert("Session issue: Academy ID missing. Please refresh the page.");
      return;
    }

    const newUser = {
      name: userForm.name,
      employeeId: userForm.employeeId,
      email: userForm.email.toLowerCase(),
      password: userForm.password,
      academyId: academyId,
      departmentId: String(deptId).trim(),
      divisionId:
        activeTab === "Supervisor" || activeTab === "Technician"
          ? selectedUserDivision?.value
          : null,
      roleId: getRoleIdForTab(),
    };

    console.log("🚀 Payload to be sent:", newUser);

    try {
      await createUser(newUser).unwrap();
      showSuccessToast("User created successfully");
      resetUserForm();
      setShowModal(false);
      await refetchUsers();
    } catch (err) {
      let errorMessage = "Something went wrong. Please try again.";

      if (err?.status === "PARSING_ERROR") {
        errorMessage = err?.data || errorMessage;
      } else if (err?.data?.message) {
        errorMessage = err.data.message;
      }

      showErrorAlert(errorMessage);
    }
  }, [
    userForm,
    selectedDepartment,
    selectedUserDivision,
    activeTab,
    academyId,
    createUser,
    refetchUsers,
    resetUserForm,
    showSuccessToast,
    showErrorAlert,
    getRoleIdForTab,
    setShowModal,
    usersData,
  ]);

  const handleUpdateUser = useCallback(async () => {
    const isRequiredFieldMissing =
      !userForm.name ||
      !userForm.email ||
      !userForm.employeeId ||
      !selectedDepartment ||
      ((activeTab === "Supervisor" || activeTab === "Technician") &&
        !selectedUserDivision);

    if (isRequiredFieldMissing) {
      showErrorAlert("Please fill in all required fields");
      return;
    }

    if (!isValidEmail(userForm.email)) {
      showErrorAlert("Please enter a valid email address");
      return;
    }

    if (!academyId) {
      showErrorAlert(
        "Unable to determine your academy. Please try logging in again."
      );
      return;
    }

    try {
      const userData = {
        name: userForm.name,
        email: userForm.email.toLowerCase(),
        employeeId: userForm.employeeId,
        departmentId: selectedDepartment.value,
        divisionId:
          activeTab === "Supervisor" || activeTab === "Technician"
            ? selectedUserDivision?.value
            : null,
        academyId: academyId,
        roleId: getRoleIdForTab(),
      };

      // Add password only if it's provided
      if (userForm.password) {
        userData.password = userForm.password;
      }

      await updateUser({ id: selectedUserId, ...userData }).unwrap();
      showSuccessToast("User updated successfully");
      setShowModal(false);
      resetUserForm();
      refetchUsers();
    } catch (error) {
      showErrorAlert(
        error?.data?.message || "Failed to update user. Please try again."
      );
    }
  }, [
    userForm,
    selectedDepartment,
    selectedUserDivision,
    activeTab,
    isValidEmail,
    academyId,
    getRoleIdForTab,
    selectedUserId,
    updateUser,
    showSuccessToast,
    showErrorAlert,
    setShowModal,
    resetUserForm,
    refetchUsers,
  ]);

  const handleDeleteUser = useCallback(
    async (
      userId,
      showConfirmDialog,
      softDeleteUser,
      refetchUsers,
      showSuccessToast,
      showErrorAlert
    ) => {
      const result = await showConfirmDialog(
        "Are you sure?",
        "Do you want to delete this user?"
      );

      if (result.isConfirmed) {
        try {
          await softDeleteUser(userId).unwrap();
          showSuccessToast("User has been deleted.");
          refetchUsers();
        } catch (error) {
          showErrorAlert("Failed to delete user");
        }
      }
    },
    []
  );

  const openEditUserModal = useCallback(
    (
      user,
      departments,
      divisions,
      setUserForm,
      setSelectedDepartment,
      setSelectedUserDivision,
      setOriginalUserData,
      setSelectedUserId,
      setIsEditingUser,
      setShowModal
    ) => {
      // Set user form with existing user data
      const userFormData = {
        name: user.name || "",
        email: user.email || "",
        password: "", // Don't pre-fill password
        employeeId: user.employeeId || "",
      };
      setUserForm(userFormData);

      // Set selected department first
      const userDepartment = departments?.find(
        (d) => d.departmentId === user.departmentId
      );

      let selectedDept = null;
      if (userDepartment) {
        selectedDept = {
          value: userDepartment.departmentId,
          label: userDepartment.name,
        };
        setSelectedDepartment(selectedDept);
      }

      // Set selected division
      let selectedDiv = null;
      if (user.divisionId) {
        console.log("User divisionId:", user.divisionId);
        console.log("Available divisions:", divisions);
        const userDivision = divisions?.find(
          (d) => d.divisionId === user.divisionId || d._id === user.divisionId
        );
        console.log("Found userDivision:", userDivision);
        if (userDivision) {
          selectedDiv = {
            value: userDivision.divisionId || userDivision._id,
            label: userDivision.divisionName || userDivision.name,
          };
          console.log("Setting selectedDiv:", selectedDiv);
          setSelectedUserDivision(selectedDiv);
        }
      } else {
        setSelectedUserDivision(null);
      }

      // Original data will be set by useEffect in the main hook

      setSelectedUserId(user.userId);
      setIsEditingUser(true);
      setShowModal(true);
    },
    []
  );

  return {
    handleCreateUser,
    handleUpdateUser,
    handleDeleteUser,
    openEditUserModal,
  };
};
